import React, { useEffect, useRef, useState } from "react";
import UIkit from "uikit";
import { usePermissionsStore } from "./permissions_store";
import rolesService from "../../services/roles.service";

const isTemporaryFallbackRole = (role) =>
  String(role?.name || role?.role_name || "").trim().toLowerCase() === "no assigned role";

const getRoleId = (role) => role?.id ?? role?.role_id ?? null;
const getAssignedRoleId = (roles, fallbackId = null) => {
  const assignedRole = roles?.[0] || null;
  if (isTemporaryFallbackRole(assignedRole)) {
    return fallbackId;
  }
  return getRoleId(assignedRole);
};

/**
 * UserRolesModal
 * Renders a modal to assign a single role for a team member.
 *
 * Props:
 *   recipient     – { recipient_id, first_name, last_name, email, roles: [] }
 *   onClose()     – called when modal is dismissed
 *   onSaved()     – called after successful save
 */
export default function UserRolesModal({ recipient, onClose, onSaved }) {
  const {
    roles,
    isOwner,
    rolesLoaded,
    loadRoles,
    setRecipientRole,
    loadEffectivePermissions,
  } = usePermissionsStore();

  const fallbackRole = roles.find(isTemporaryFallbackRole)
    || recipient.roles?.find(isTemporaryFallbackRole)
    || null;
  const fallbackRoleId = getRoleId(fallbackRole);
  const visibleRoles = roles.filter((role) => !isTemporaryFallbackRole(role) && (isOwner || !role.grants_admin));

  const [selectedId, setSelectedId] = useState(
    () => getAssignedRoleId(recipient.roles, fallbackRoleId),
  );
  const [assignmentLoaded, setAssignmentLoaded] = useState(false);
  const [assignmentError, setAssignmentError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recipientRoles, setRecipientRoles] = useState(() => recipient.roles || []);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!rolesLoaded) loadRoles();
  }, [rolesLoaded, loadRoles]);

  useEffect(() => {
    setRecipientRoles(recipient.roles || []);
  }, [recipient]);

  useEffect(() => {
    let cancelled = false;
    setAssignmentLoaded(false);
    setAssignmentError(false);

    const syncRecipientRoles = async () => {
      try {
        const response = await rolesService.getRecipientRoles(recipient.recipient_id);
        if (cancelled) {
          return;
        }

        const nextRoles = response?.data?.roles || response?.data || [];
        setRecipientRoles(Array.isArray(nextRoles) ? nextRoles : []);
        setAssignmentLoaded(true);
      } catch (err) {
        if (!cancelled) {
          setAssignmentError(true);
          console.error("[permissions] failed to load recipient roles", err);
        }
      }
    };

    syncRecipientRoles();

    return () => {
      cancelled = true;
    };
  }, [recipient.recipient_id]);

  useEffect(() => {
    setSelectedId(getAssignedRoleId(recipientRoles, fallbackRoleId));
  }, [fallbackRoleId, recipientRoles]);

  const closePanel = () => {
    onClose?.();
  };

  const handleSave = async () => {
    if (!rolesLoaded || !assignmentLoaded || saving) return;
    setSaving(true);
    try {
      await setRecipientRole(recipient.recipient_id, selectedId);
      await loadEffectivePermissions();
      UIkit.notification("Role updated", { status: "success" });
      onSaved?.();
      closePanel();
    } catch (error) {
      UIkit.notification(error?.response?.data?.error || "Failed to update role", { status: "danger" });
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  const name = [recipient.first_name, recipient.last_name].filter(Boolean).join(" ") || recipient.email;

  return (
    <div
      id="team-user-roles"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div>
          <button className="uk-offcanvas-close uk-close-large" type="button" data-uk-close onClick={closePanel} />

          <div className="uk-modal-header">
            <h2 className="uk-modal-title">Assign Role — {name}</h2>
            <p className="secondary uk-margin-small-top">{recipient.email}</p>
          </div>

          <div className="uk-modal-body">
            {assignmentError ? (
              <p role="alert">Unable to load the current role. Close and reopen this panel to retry.</p>
            ) : !rolesLoaded || !assignmentLoaded ? (
              <div className="uk-text-center uk-padding">Loading…</div>
            ) : (
              <div>
                {selectedId != null && !visibleRoles.some((role) => role.id === selectedId) && (
                  <p role="status">Current role: {recipientRoles[0]?.name || recipientRoles[0]?.role_name || "restricted role"}. Choose a role below to replace it.</p>
                )}
                <label
                  className="uk-flex uk-flex-middle"
                  style={{
                    padding: "10px 0",
                    borderBottom: "1px solid #f0f0f0",
                    cursor: "pointer",
                    gap: 12,
                  }}
                >
                  <input
                    type="radio"
                    name="team-user-role"
                    className="uk-radio"
                    checked={selectedId == null}
                    onChange={() => setSelectedId(null)}
                  />
                  <div>
                    <div className="semi-bold-font">No access</div>
                    <div className="secondary">Remove the assigned role. This user will have no section access.</div>
                  </div>
                </label>
                {visibleRoles.map((role) => {
                  const checked = selectedId === role.id;
                  return (
                    <label
                      key={role.id}
                      className="uk-flex uk-flex-middle"
                      style={{
                        padding: "10px 0",
                        borderBottom: "1px solid #f0f0f0",
                        cursor: "pointer",
                        gap: 12,
                      }}
                    >
                      <input
                        type="radio"
                        name="team-user-role"
                        className="uk-radio"
                        checked={checked}
                        onChange={() => setSelectedId(role.id)}
                      />
                      <div>
                        <div className="semi-bold-font">
                          {role.name}
                          {role.is_system_role && (
                            <span
                              className="hms-booking-tag uk-margin-small-left"
                            >
                              System
                            </span>
                          )}
                        </div>
                        {role.description && (
                          <div className="secondary">{role.description}</div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            <div
              className="uk-flex uk-flex-between uk-flex-middle"
            >
              <div className="uk-width-1-1 uk-margin-top uk-grid-margin uk-first-column">
                <button
                  className="uk-button hms-btn hms-red-btn"
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !rolesLoaded || !assignmentLoaded}
                >
                  {saving ? "Saving…" : "Save Role"}
                </button>
                <button className="uk-button hms-btn uk-offcanvas-close" type="button" onClick={closePanel}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
